/**
 * Simple Form.io Translation Service
 *
 * Fetches translations from Form.io language resource API via secure server route with:
 * - Simple in-memory caching
 * - Fallback to DEFAULT_LOCALE
 * - Smart label formatting from keys
 */

import formioEnglishDefaults from '@formio/js/lib/cjs/translations/en';
import { DEFAULT_LOCALE } from '@/constants/locales';
import { omitEmptyValues } from '@/utils/formio-translation-merge';

// Simple in-memory cache
const translationCache: Record<string, Record<string, string>> = {};
const fetchPromises: Record<string, Promise<Record<string, string>>> = {};

/**
 * Formats a translation key into a readable label
 * Example: "form.label.organizationLegalName" → "Organization Legal Name"
 */
function formatKeyAsLabel(key: string): string {
  const lastPart = key.split('.').pop() || key;
  return lastPart
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

/**
 * Fetches language resource submissions from Form.io via secure server API
 */
async function fetchLanguageResource(locale: string): Promise<Record<string, string>> {
  // Check cache first
  if (translationCache[locale]) {
    return translationCache[locale];
  }

  // Check if already fetching
  const existingPromise = fetchPromises[locale];
  if (existingPromise) {
    return existingPromise;
  }

  // Fetch from API via server route (secure)
  fetchPromises[locale] = (async () => {
    try {
      const response = await fetch(
        `/api/formio-translations?locale=${encodeURIComponent(locale)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          cache: 'no-store',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const translations = (await response.json()) as Record<string, string>;

      if (!translations || typeof translations !== 'object' || Array.isArray(translations)) {
        console.warn(`[FormioTranslation] Unexpected response shape for locale "${locale}"`);
        return {};
      }

      if (Object.keys(translations).length === 0) {
        console.warn(`[FormioTranslation] No translation found for locale "${locale}"`);
        return {};
      }

      translationCache[locale] = translations;
      return translations;
    } catch (error) {
      console.error(`[FormioTranslation] Error fetching translations for "${locale}":`, error);
      return {};
    } finally {
      delete fetchPromises[locale];
    }
  })();

  return fetchPromises[locale];
}

/**
 * Gets all translations for a locale (for Form.io i18n config)
 */
export async function getTranslationsForLocale(locale: string): Promise<Record<string, string>> {
  const translations = await fetchLanguageResource(locale);

  // For non-default locales, English sits between the formatted fallback and
  // the current locale. For the default locale there is no separate English
  // layer (it is English), so that layer is empty.
  const defaultTranslations =
    locale !== DEFAULT_LOCALE ? await fetchLanguageResource(DEFAULT_LOCALE) : {};

  // Create formatted labels for all keys as the base fallback
  const allKeys = new Set([...Object.keys(defaultTranslations), ...Object.keys(translations)]);

  const formattedFallbacks: Record<string, string> = {};
  allKeys.forEach((key) => {
    formattedFallbacks[key] = formatKeyAsLabel(key);
  });

  return {
    // Base: Form.io's built-in English strings (fileTooBig, fileTooSmall, the
    // wrong-file-type message, etc.). This project's locales ("en-us", "es")
    // don't match Form.io's dictionary languages ("en"/"de"), so Form.io's i18n
    // never merges its own defaults in for our locales — without this layer those
    // internal keys fall through and render as the raw key (e.g. "fileTooBig").
    ...formioEnglishDefaults,
    ...formattedFallbacks, // formatted labels for CMS keys
    ...omitEmptyValues(defaultTranslations), // Middle: English translations
    ...omitEmptyValues(translations), // Top: Current locale translations (empty values fall through)
  };
}
